import { useEffect, useRef, useState } from "react";

import { ILocationInfo, IMapLocationTracker } from "../Features/LocationInfo/types";
import { IMapSettings } from "../Settings/settingsTypes";
import { IInstantNotifier } from "../Lib/Notifier/types";
import { ILog } from "../Lib/Log/types";
import { geolocationPositionToLocation } from "./locationLogic";


const useMapLocationTracker = (settings: IMapSettings, notifier: IInstantNotifier, log: ILog): IMapLocationTracker => {

    const maxPositionAgeMs = 2000;

    const isSupported = !!navigator.geolocation;
    const [isEnabled, setIsEnabled] = useState<boolean>(false);
    const [isTrackingActive, setIsTrackingActive] = useState<boolean>(false);
    const [location, setLocation] = useState<ILocationInfo | null>(null);

    const watchId = useRef<number | null>(null);

    const disposeWatch = () => {

        if (watchId.current)
            navigator.geolocation.clearWatch(watchId.current);

    };
    
    useEffect(() => {

        if (!isSupported)
            return;

        disposeWatch();
        watchId.current = navigator.geolocation.watchPosition(onPositionChange, onPositionError, { enableHighAccuracy: true, maximumAge: maxPositionAgeMs });

        return () => {
            disposeWatch();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleTracking = () => {

        if (!isSupported || !isEnabled)
            throw new Error("Location tracking is not enabled");

        setIsTrackingActive(!isTrackingActive);
    };

    const onPositionChange = (position: GeolocationPosition) => {
        setIsEnabled(true);
        setLocation(geolocationPositionToLocation(position));
    }

    const onPositionError = (error: GeolocationPositionError) => {
        if (error.code === error.PERMISSION_DENIED) {
            setIsEnabled(false);
            setIsTrackingActive(false);
            notifier.error('Unable to access your location. Please check browser permissions.', error);
        }
    }

    return {
        isSupported,
        isEnabled,
        isTrackingActive,
        locationTrackingBehaviour: settings.locationTrackingBehaviour,
        toggleTracking,
        location
    };
};

export default useMapLocationTracker;